import { ApolloClient, ApolloLink, ApolloProvider, HttpLink, InMemoryCache, TypePolicies } from "@apollo/client";
import { WatchQueryFetchPolicy } from "@apollo/client/core/watchQueryOptions";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { useMsal } from "@azure/msal-react";
import * as React from "react";
import { AUTH_ISSUER_CYPRESS_COOKIE_NAME, buildAuthorizationHeader, getToken, isTestEnv } from "../../auth/utils/utils";
import { getCookie } from "../../helper/handleCookies/handleCookies";
import { getUserPreferredLanguage } from "../../helper/userPreferredLanguage/userPreferredLanguage";
import { ErrorMessages } from "../errors/ErrorTypes";
import promiseToObservable from "./promiseToObservable";

type StateProviderProps = {
  fetchPolicy: WatchQueryFetchPolicy;
  typePolicies?: TypePolicies;
};

const StateProvider: React.FC<StateProviderProps> = ({ fetchPolicy, typePolicies, children }) => {
  const [hasNetworkError, setHasNetworkError] = React.useState(false);
  const [hasNotFoundError, setHasNotFoundError] = React.useState(false);
  const [hasAuthError, setHasAuthError] = React.useState(false);

  const { inProgress } = useMsal();

  const language = getUserPreferredLanguage();

  const authLink = setContext(async () => {
    if (isTestEnv()) {
      // authToken is only set for e2e tests (Basic auth)
      // Otherwise get the id token from the authProvider
      return {
        headers: {
          authorization: getCookie(AUTH_ISSUER_CYPRESS_COOKIE_NAME),
          language,
        },
      };
    }

    const token = await getToken(inProgress, false);

    return {
      headers: {
        ...buildAuthorizationHeader(token ? token.idToken : "").headers,
        language,
      },
    };
  });

  const authErrorLink = onError(({ graphQLErrors, operation, forward }) => {
    const has401 = graphQLErrors?.some(({ extensions }) => {
      return extensions?.errorCode === 401;
    });

    if (has401) {
      // @ts-ignore
      return promiseToObservable(getToken(undefined, true)).flatMap((token) => {
        operation.setContext({
          headers: {
            // @ts-ignore
            ...buildAuthorizationHeader(token.idToken).headers,
            language,
          },
          isRetry: true,
        });

        return forward(operation);
      });
    }
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(async ({ message, locations, path, extensions }) => {
        if (extensions?.errorCode === 404) {
          setHasNotFoundError(true);
        }
        if (extensions?.errorCode === 401) {
          setHasAuthError(true);
        }

        if (extensions?.errorCode === 403) {
          setHasAuthError(true);
        }

        console.warn(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
      });
    }

    if (networkError) {
      console.warn(`[Network error]: ${networkError}`);
      setHasNetworkError(true);
    }
  });

  const httpLink = new HttpLink({
    uri: process.env.GQL_API || "/graphql",
    credentials: "same-origin",
  });

  const client = new ApolloClient({
    link: ApolloLink.from([authLink, authErrorLink, errorLink, httpLink]),
    cache: new InMemoryCache({
      typePolicies: {
        ...typePolicies,
      },
    }),
    defaultOptions: {
      watchQuery: {
        fetchPolicy,
      },
    },
  });

  if (hasNetworkError) {
    throw new Error(ErrorMessages.e500);
  }

  if (hasNotFoundError) {
    throw new Error(ErrorMessages.e404);
  }

  if (hasAuthError) {
    throw new Error(ErrorMessages.e403);
  }

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default StateProvider;
